/**
 * Shared constants for utility functions across the Tellescope monorepo.
 * Centralizes configuration values to prevent magic numbers and improve maintainability.
 */

/**
 * Default batch processing configuration
 */
export const BATCH_PROCESSING = {
  /**
   * Default number of items to process in a single batch
   * @default 100
   */
  DEFAULT_BATCH_SIZE: 100,

  /**
   * Default maximum number of concurrent batches to process in parallel
   * @default 3
   */
  DEFAULT_CONCURRENCY: 3,

  /**
   * Default behavior for error handling in batch processing
   * @default true - Continue processing other batches if one fails
   */
  DEFAULT_CONTINUE_ON_ERROR: true,
} as const;

/**
 * Object comparison and diff configuration
 */
export const OBJECT_DIFF = {
  /**
   * Maximum depth for recursive object comparison to prevent infinite recursion
   * @default 10
   */
  DEFAULT_MAX_DEPTH: 10,

  /**
   * Default behavior for including removed fields in diff results
   * @default false
   */
  DEFAULT_INCLUDE_REMOVALS: false,
} as const;

/**
 * Performance thresholds and limits
 */
export const PERFORMANCE = {
  /**
   * Maximum number of items to process before considering performance optimizations
   * @default 10000
   */
  LARGE_ARRAY_THRESHOLD: 10000,

  /**
   * Recommended batch size for large datasets
   * @default 1000
   */
  LARGE_DATASET_BATCH_SIZE: 1000,
} as const;

/**
 * API and network related constants
 */
export const NETWORK = {
  /**
   * Default timeout for debounced operations (in milliseconds)
   * @default 300
   */
  DEFAULT_DEBOUNCE_TIMEOUT: 300,

  /**
   * Recommended batch size for API operations to avoid rate limits
   * @default 50
   */
  API_BATCH_SIZE: 50,
} as const;

/**
 * Deep merge configuration
 */
export const DEEP_MERGE = {
  /**
   * Maximum depth for recursive object merging to prevent infinite recursion
   * @default 10
   */
  DEFAULT_MAX_DEPTH: 10,

  /**
   * Default behavior for array cloning during merge
   * @default false - Arrays are replaced, not cloned
   */
  DEFAULT_CLONE_ARRAYS: false,

  /**
   * Default behavior for array merging during merge
   * @default false - Source arrays replace target arrays
   */
  DEFAULT_MERGE_ARRAYS: false,
} as const;
